import React, { memo } from 'react';
import Button from '@atlaskit/button';
import SettingsIcon from '@atlaskit/icon/core/migration/settings--editor-settings';
import Tooltip from '@atlaskit/tooltip';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import {
	CONFIG_LINK_MODE_DISABLED_NO_REQUEST_TYPE,
	CONFIG_LINK_MODE_DISABLED_PERMISSION,
	CONFIG_LINK_MODE_HIDE,
	CONFIG_LINK_MODE_SHOW,
	CONFIG_LINK_MODE_DISABLED_NO_SCREEN_ID,
	type ConfigLinkMode,
} from '@atlassian/jira-issue-view-store/src/selectors/issue-actions-selector.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/react-resource-router';
import issueActionViewMessages from '../issue-actions/messages.tsx';
import configLinkMode from '../utils/config-link-mode.tsx';
import getConfigurationHref from '../utils/get-configuration-href.tsx';
import messages from './messages.tsx';
import Wrapper from './styled.tsx';

const configButtonOptionsFromMode = (
	mode: ConfigLinkMode,
	formatMessage: IntlShape['formatMessage'],
) => {
	switch (mode) {
		case CONFIG_LINK_MODE_SHOW:
			return {
				tooltipText: formatMessage(messages.configureTooltipAdmin),
				isDisabled: false,
			};
		case CONFIG_LINK_MODE_DISABLED_PERMISSION:
			return {
				tooltipText: formatMessage(messages.configureTooltipEndUser),
				isDisabled: true,
			};
		case CONFIG_LINK_MODE_DISABLED_NO_REQUEST_TYPE:
			return {
				tooltipText: formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.configureTooltipAdminNoRequestTypeIssueTermRefresh
						: messages.configureTooltipAdminNoRequestType,
				),
				isDisabled: true,
			};
		case CONFIG_LINK_MODE_DISABLED_NO_SCREEN_ID:
			return {
				tooltipText: '',
				isDisabled: true,
			};
		case CONFIG_LINK_MODE_HIDE:
		default:
			return {
				isHidden: true,
			};
	}
};

/**
 * Configure Fields Button
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ConfigurationButton = memo<Record<any, any>>(() => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const configurationLinkMode = configLinkMode();

	const configurationButtonOptions = configButtonOptionsFromMode(
		configurationLinkMode,
		formatMessage,
	);

	if (configurationButtonOptions.isHidden) {
		return null;
	}

	const { tooltipText, isDisabled } = configurationButtonOptions;
	const buttonText = formatMessage(issueActionViewMessages.configure);
	const href = getConfigurationHref(formatMessage);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onClick = (event: any, analyticsEvent: any) => {
		event.preventDefault(); // This is required as CMP's event propogation triggers full page reload
		fireUIAnalytics(analyticsEvent, 'configureButton');
		push(href);
	};

	return (
		<ErrorBoundary prefixOverride="issue" id="Safe(ConfigurationButton)">
			<Wrapper>
				<Tooltip position="mouse" mousePosition="left" content={tooltipText}>
					<Button
						/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
						testId="issue.views.issue-base.foundation.configuration-button.configuration-button"
						isDisabled={isDisabled}
						appearance="subtle-link"
						iconBefore={<SettingsIcon color="currentColor" label="" />}
						href={href}
						onClick={onClick}
						interactionName="auto-generated.issue.views.issue-base.foundation.configuration-button.configuration-button.button"
					>
						{buttonText}
					</Button>
				</Tooltip>
			</Wrapper>
		</ErrorBoundary>
	);
});
